/**
 * Модалка инициирования диалога с пациентом
 * Дата: 31.01.2026 - Создан для TODO-051 (Инициирование диалога на странице /messages)
 * Дата: 31.01.2026 - Итерации 2-4: проверка переписки, привязки, отправка сообщения
 * Дата: 05.02.2026 - TODO-055: Фильтрация неподключенных мессенджеров
 * 
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - Messages.tsx - основная страница сообщений, открывает эту модалку
 * - messagesApi (api/messages.ts) - API методы для работы с сообщениями
 * - Modal.tsx (components/ui/Modal.tsx) - базовый компонент модалки
 * - Input.tsx, Select.tsx, Button.tsx, Textarea.tsx (components/ui/) - UI компоненты
 * - channels.ts (config/channels.ts) - конфигурация каналов
 * - useToast (hooks/useToast.ts) - уведомления
 * - hooks/useTenantConfig.ts - конфигурация tenant с подключенными мессенджерами
 * 
 * КАК РАБОТАЕТ:
 * 1. Пользователь нажимает "Начать диалог" на странице /messages
 * 2. Открывается модалка с полем ввода телефона и выбором канала
 * 3. Каналы фильтруются — показываются только подключенные (05.02.2026)
 * 4. При выборе канала выполняется проверка существующей переписки
 * 5. Если переписка есть - показывается предупреждение и кнопка "Открыть"
 * 6. Если переписки нет - проверяется привязка (для Telegram/MAX)
 * 7. Если привязки нет - показывается предупреждение и ссылка на /phone-binding
 * 8. Если все ок - показывается textarea для текста сообщения
 * 9. При отправке - вызывается messagesApi.sendMessage()
 * 10. После успешной отправки - открывается созданный чат
 * 
 * СХЕМА:
 * ┌─────────────────────────────────────────────────────────────┐
 * │  Messages.tsx                                               │
 * │       │                                                     │
 * │       ├──► [Кнопка "Начать диалог"]                        │
 * │       │         │                                           │
 * │       │         ▼                                           │
 * │       └──► InitiateConversationModal.tsx                   │
 * │                 │                                           │
 * │                 ├──► Input (телефон)                        │
 * │                 ├──► Select (канал)                        │
 * │                 │         │                                 │
 * │                 │         ├──► checkConversationExists()    │
 * │                 │         │         │                       │
 * │                 │         │         ├── ДА ──► "Переписка существует" │
 * │                 │         │         │                       │
 * │                 │         │         └── НЕТ ──► getPatientBindings() │
 * │                 │         │                   │             │
 * │                 │         │                   ├── НЕТ ──► "Нужна привязка" │
 * │                 │         │                   │             │
 * │                 │         │                   └── ДА ──► Textarea │
 * │                 │         │                             │     │
 * │                 │         │                             └──► sendMessage() │
 * │                 │         │                                         │
 * │                 │         └──► onOpenConversation(phone)          │
 * │                 │                                                 │
 * └─────────────────────────────────────────────────────────────┘
 * 
 * ИСПОЛЬЗОВАНИЕ ИЗ ДРУГИХ МОДУЛЕЙ:
 * - Messages.tsx - открывает модалку при клике на кнопку "Начать диалог"
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Input, Select, Button, Textarea } from '@/components/ui';
import { CHANNELS, type ChannelId } from '@/config/channels';
import { messagesApi } from '@/api/messages';
import * as telegramClientApi from '@/api/telegramClient';
import * as maxClientApi from '@/api/maxClient';
import { useToast } from '@/hooks/useToast';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { usePhoneSearch } from '@/hooks/usePhoneBinding';
import { MessageSquare, AlertCircle, CheckCircle, ExternalLink, Loader2, Phone, AtSign, Settings, Search } from 'lucide-react';

interface InitiateConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId?: string;
  onOpenConversation?: (phone: string) => void;
}

type CheckState =
  | 'idle'
  | 'checking'
  | 'exists'
  | 'no_binding'
  | 'ready'
  | 'tg_found'
  | 'tg_not_found'
  | 'tg_search_found'
  | 'tg_search_not_found'
  | 'max_found'
  | 'max_not_found'
  | 'max_search_found'
  | 'max_search_not_found';
type InputMode = 'phone' | 'messenger_id';

/**
 * Валидация Telegram ID или username
 * Дата: 05.02.2026 - TODO-051: Поддержка TG ID для инициирования диалога
 * Принимает: числовой ID (7104785699) или username (@SmileProSkills или SmileProSkills)
 */
function validateTelegramId(input: string): { isValid: boolean; type: 'id' | 'username'; value: string; error?: string } {
  const cleaned = input.trim();
  
  if (!cleaned) {
    return { isValid: false, type: 'id', value: '', error: 'Введите Telegram ID или username' };
  }
  
  // Проверка на числовой ID
  if (/^\d+$/.test(cleaned)) {
    return { isValid: true, type: 'id', value: cleaned };
  }
  
  // Проверка на username (с @ или без)
  const username = cleaned.startsWith('@') ? cleaned.slice(1) : cleaned;
  // Username: 5-32 символа, буквы, цифры, подчёркивания
  if (/^[a-zA-Z][a-zA-Z0-9_]{4,31}$/.test(username)) {
    return { isValid: true, type: 'username', value: username };
  }
  
  return { 
    isValid: false, 
    type: 'id', 
    value: cleaned, 
    error: 'Введите числовой ID (например 7104785699) или username (например @SmileProSkills)' 
  };
}

/** MAX Client: только числовой chat ID (max:123 или 123). @username не поддерживается. */
function validateMaxChatId(input: string): { isValid: boolean; value: string; error?: string } {
  const raw = input.trim().replace(/^max:/i, '').replace(/^@/, '');
  if (!raw) {
    return { isValid: false, value: '', error: 'Введите MAX chat ID' };
  }
  if (!/^\d+$/.test(raw)) {
    return {
      isValid: false,
      value: raw,
      error: 'Формат: числовой chat ID (например 123456789 или max:123456789)',
    };
  }
  return { isValid: true, value: raw };
}

/**
 * Валидация номера телефона
 * Принимает телефон в любом формате, нормализует и проверяет
 */
function validatePhone(phone: string): { isValid: boolean; normalized?: string; error?: string } {
  // Удаляем все нецифровые символы кроме +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Если начинается с +, оставляем, иначе добавляем +7 для российских номеров
  let normalized = cleaned;
  if (!normalized.startsWith('+')) {
    // Если начинается с 8, заменяем на +7
    if (normalized.startsWith('8')) {
      normalized = '+7' + normalized.slice(1);
    } else if (normalized.startsWith('7')) {
      normalized = '+' + normalized;
    } else {
      normalized = '+7' + normalized;
    }
  }
  
  // Удаляем + для проверки длины
  const digitsOnly = normalized.replace(/\D/g, '');
  
  // Проверка длины (российский номер: 11 цифр после +7)
  if (digitsOnly.length < 10 || digitsOnly.length > 12) {
    return {
      isValid: false,
      error: 'Номер телефона должен содержать от 10 до 12 цифр',
    };
  }
  
  return {
    isValid: true,
    normalized,
  };
}

/**
 * Каналы, доступные для инициирования диалога
 * Дата: 01.02.2026 - Добавлен whatsapp_regular (Wazzup API позволяет писать первым)
 */
const INITIATABLE_CHANNELS: ChannelId[] = [
  'whatsapp_waba',
  'whatsapp_regular', // Wazzup API позволяет инициировать диалог первым
  'telegram_bot',
  'telegram_client',
  'max_client',
  'max',
  'sms',
  'sms_tel',
];

export function InitiateConversationModal({
  isOpen,
  onClose,
  tenantId,
  onOpenConversation,
}: InitiateConversationModalProps) {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  
  // Дата: 05.02.2026 - TODO-055: Получаем конфигурацию tenant для фильтрации каналов
  const { messengerStatus, isLoading: isConfigLoading } = useTenantConfig(tenantId);
  
  const [phone, setPhone] = useState('');
  const [telegramInput, setTelegramInput] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [messageText, setMessageText] = useState('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [telegramError, setTelegramError] = useState<string>('');
  const [maxInput, setMaxInput] = useState('');
  const [maxError, setMaxError] = useState<string>('');
  const [checkState, setCheckState] = useState<CheckState>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bindingError, setBindingError] = useState<string>('');
  // Дата: 05.02.2026 - TODO-051: режим ввода для TG Client
  const [inputMode, setInputMode] = useState<InputMode>('phone');
  const [foundTgUser, setFoundTgUser] = useState<telegramClientApi.UserInfo | null>(null);
  const [foundMaxUser, setFoundMaxUser] = useState<maxClientApi.UserInfo | null>(null);
  const [isMaxSearching, setIsMaxSearching] = useState(false);

  // Поиск в Telegram по номеру (MTProto через TG Client) — аналог TelegramLinkPanel
  const { search: searchInTg, isSearching: isTgSearching, result: tgPhoneSearchResult } = usePhoneSearch({
    onFound: (r) => {
      showSuccess(`Найден в Telegram: ${r.first_name || ''}${r.telegram_username ? ` (@${r.telegram_username})` : ''}, ID: ${r.telegram_user_id}`);
      setCheckState('tg_search_found');
    },
    onNotFound: () => {
      showError('Пользователь не найден в Telegram (не зарегистрирован или приватность скрыта)');
      setCheckState('tg_search_not_found');
    },
    onError: (err) => {
      showError(`Ошибка поиска: ${err.message}`);
      setCheckState('tg_search_not_found');
    },
  });

  const handleSearchInTg = () => {
    const validation = validatePhone(phone);
    if (!validation.isValid || !validation.normalized) return;
    const digits = validation.normalized.replace(/\D/g, '');
    searchInTg(digits);
  };

  const handleSearchInMax = async () => {
    const validation = validatePhone(phone);
    if (!validation.isValid || !validation.normalized) return;
    setIsMaxSearching(true);
    setBindingError('');
    try {
      const digits = validation.normalized.replace(/\D/g, '');
      const info = await maxClientApi.searchByPhone(digits, tenantId);
      if (info.found) {
        setFoundMaxUser(info);
        setCheckState('max_search_found');
        showSuccess(
          `Найден в MAX: ${info.first_name || ''} ${info.last_name || ''}, chat ID: ${info.chat_id ?? info.user_id}`,
        );
      } else {
        setFoundMaxUser(null);
        setCheckState('max_search_not_found');
        showError('Пользователь не найден в MAX по этому номеру');
      }
    } catch (error: any) {
      setCheckState('max_search_not_found');
      showError(error?.response?.data?.error || error?.message || 'Ошибка поиска в MAX');
    } finally {
      setIsMaxSearching(false);
    }
  };

  // Сброс формы при закрытии
  const handleClose = () => {
    setPhone('');
    setTelegramInput('');
    setMaxInput('');
    setSelectedChannel('');
    setMessageText('');
    setPhoneError('');
    setTelegramError('');
    setMaxError('');
    setCheckState('idle');
    setBindingError('');
    setIsSubmitting(false);
    setInputMode('phone');
    setFoundTgUser(null);
    setFoundMaxUser(null);
    setIsMaxSearching(false);
    onClose();
  };

  // Валидация телефона при изменении
  const handlePhoneChange = (value: string) => {
    setPhone(value);
    setPhoneError('');
    setCheckState('idle');
    setBindingError('');
    
    if (value && selectedChannel) {
      const validation = validatePhone(value);
      if (!validation.isValid) {
        setPhoneError(validation.error || 'Неверный формат телефона');
      }
    }
  };

  // Проверка переписки и привязки при выборе канала
  // Дата: 05.02.2026 - TODO-051: добавлена логика для режима telegram_id
  useEffect(() => {
    const isTgClientWithIdInput =
      selectedChannel === 'telegram_client' && inputMode === 'messenger_id';
    const isMaxClientWithIdInput =
      selectedChannel === 'max_client' && inputMode === 'messenger_id';

    if (isTgClientWithIdInput) {
      if (!telegramInput || !selectedChannel) {
        setCheckState('idle');
        setBindingError('');
        setFoundTgUser(null);
        return;
      }

      const validation = validateTelegramId(telegramInput);
      if (!validation.isValid) {
        setCheckState('idle');
        return;
      }

      const searchTgUser = async () => {
        setCheckState('checking');
        setBindingError('');
        setFoundTgUser(null);

        try {
          let userInfo: telegramClientApi.UserInfo;
          if (validation.type === 'username') {
            userInfo = await telegramClientApi.searchByUsername(validation.value, tenantId);
          } else {
            userInfo = await telegramClientApi.searchByTgId(validation.value, tenantId);
          }

          if (userInfo.found) {
            setFoundTgUser(userInfo);
            setCheckState('tg_found');
          } else {
            setCheckState('tg_not_found');
            setTelegramError(userInfo.reason || 'Пользователь не найден в Telegram');
          }
        } catch (error: any) {
          console.error('Ошибка при поиске TG пользователя:', error);
          setCheckState('tg_not_found');
          setTelegramError(error?.response?.data?.error || 'Ошибка поиска в Telegram');
        }
      };

      const timer = setTimeout(searchTgUser, 500);
      return () => clearTimeout(timer);
    }

    if (isMaxClientWithIdInput) {
      if (!maxInput || !selectedChannel) {
        setCheckState('idle');
        setBindingError('');
        setFoundMaxUser(null);
        return;
      }

      const validation = validateMaxChatId(maxInput);
      if (!validation.isValid) {
        setCheckState('idle');
        return;
      }

      const lookupMaxUser = async () => {
        setCheckState('checking');
        setBindingError('');
        setFoundMaxUser(null);

        try {
          const maxIdentifier = `max:${validation.value}`;
          const exists = await messagesApi.checkConversationExists(
            maxIdentifier,
            selectedChannel,
            tenantId,
          );
          if (exists) {
            setCheckState('exists');
            return;
          }

          const userInfo = await maxClientApi.searchByChatId(validation.value, tenantId);
          if (userInfo.found) {
            setFoundMaxUser(userInfo);
            setCheckState('max_found');
          } else {
            setCheckState('max_not_found');
            setMaxError(userInfo.reason || 'Некорректный MAX chat ID');
          }
        } catch (error: any) {
          console.error('Ошибка при проверке MAX chat ID:', error);
          setCheckState('max_not_found');
          setMaxError(error?.response?.data?.error || 'Ошибка проверки MAX chat ID');
        }
      };

      const timer = setTimeout(lookupMaxUser, 500);
      return () => clearTimeout(timer);
    }

    if (
      (selectedChannel === 'telegram_client' || selectedChannel === 'max_client') &&
      inputMode === 'messenger_id'
    ) {
      return;
    }

    // Обычная логика для телефона
    if (!phone || !selectedChannel) {
      setCheckState('idle');
      setBindingError('');
      return;
    }

    const validation = validatePhone(phone);
    if (!validation.isValid) {
      setCheckState('idle');
      return;
    }

    const checkConversation = async () => {
      setCheckState('checking');
      setBindingError('');

      try {
        // Итерация 2: Проверка существующей переписки
        const exists = await messagesApi.checkConversationExists(
          validation.normalized!,
          selectedChannel,
          tenantId
        );

        if (exists) {
          setCheckState('exists');
          return;
        }

        // Итерация 3: Проверка привязки для Telegram/MAX
        const channelConfig = CHANNELS[selectedChannel as ChannelId];
        if (channelConfig?.requiresBinding) {
          const bindingsResponse = await messagesApi.getPatientBindings(
            validation.normalized!,
            tenantId
          );

          const hasBinding = bindingsResponse.bindings?.some((b: any) => {
            if (selectedChannel === 'telegram_bot' || selectedChannel === 'telegram_client') {
              return b.messenger === 'telegram' || b.messenger === 'telegram_bot' || b.messenger === 'telegram_client';
            }
            if (selectedChannel === 'max' || selectedChannel === 'max_client') {
              return b.messenger === 'max' || b.messenger === 'max_client';
            }
            return false;
          });

          if (!hasBinding) {
            setCheckState('no_binding');
            return;
          }
        }

        // Все проверки пройдены - можно отправлять
        setCheckState('ready');
      } catch (error) {
        console.error('Ошибка при проверке:', error);
        setCheckState('ready'); // В случае ошибки разрешаем попытку отправки
      }
    };

    // Debounce проверки
    const timer = setTimeout(checkConversation, 500);
    return () => clearTimeout(timer);
  }, [phone, telegramInput, maxInput, selectedChannel, tenantId, inputMode]);

  // Открыть существующую переписку
  const handleOpenExisting = () => {
    if (
      selectedChannel === 'telegram_client' &&
      inputMode === 'messenger_id' &&
      foundTgUser?.user_id &&
      onOpenConversation
    ) {
      onOpenConversation(`tg:${foundTgUser.user_id}`);
      handleClose();
      return;
    }
    if (selectedChannel === 'max_client' && inputMode === 'messenger_id' && maxInput.trim()) {
      const validation = validateMaxChatId(maxInput);
      if (validation.isValid && onOpenConversation) {
        onOpenConversation(`max:${validation.value}`);
        handleClose();
        return;
      }
    }
    const validation = validatePhone(phone);
    if (validation.isValid && validation.normalized && onOpenConversation) {
      onOpenConversation(validation.normalized);
      handleClose();
    }
  };

  // Перейти к привязке
  const handleGoToBinding = () => {
    const validation = validatePhone(phone);
    if (validation.isValid && validation.normalized) {
      navigate(`/phone-binding?phone=${encodeURIComponent(validation.normalized)}`);
      handleClose();
    }
  };

  // Обработка отправки сообщения
  // Дата: 05.02.2026 - TODO-051: добавлена поддержка отправки по TG ID
  const handleSubmit = async () => {
    const isTgClientWithIdInput =
      selectedChannel === 'telegram_client' && inputMode === 'messenger_id';
    const isMaxClientWithIdInput =
      selectedChannel === 'max_client' && inputMode === 'messenger_id';

    if (!selectedChannel) {
      return;
    }

    if (!messageText.trim()) {
      setBindingError('Введите текст сообщения');
      return;
    }

    if (isTgClientWithIdInput) {
      if (!telegramInput.trim()) {
        setTelegramError('Введите Telegram ID или username');
        return;
      }

      if (checkState !== 'tg_found' || !foundTgUser) {
        return;
      }

      setIsSubmitting(true);
      setBindingError('');

      try {
        const tgIdentifier = `tg:${foundTgUser.user_id}`;
        await messagesApi.sendMessage({
          phone: tgIdentifier,
          channel: selectedChannel,
          content: messageText.trim(),
          content_type: 'text',
          tenant_id: tenantId,
        });

        showSuccess('Сообщение отправлено');
        if (onOpenConversation) {
          onOpenConversation(tgIdentifier);
        }
        handleClose();
      } catch (error: any) {
        console.error('Ошибка при отправке сообщения:', error);
        const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при отправке сообщения';
        showError(errorMessage);
        setBindingError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (isMaxClientWithIdInput) {
      if (!maxInput.trim()) {
        setMaxError('Введите MAX chat ID');
        return;
      }

      if (checkState !== 'max_found' || !foundMaxUser) {
        return;
      }

      setIsSubmitting(true);
      setBindingError('');

      try {
        const chatId = foundMaxUser.chat_id ?? foundMaxUser.user_id;
        const maxIdentifier = `max:${chatId}`;
        await messagesApi.sendMessage({
          phone: maxIdentifier,
          channel: selectedChannel,
          content: messageText.trim(),
          content_type: 'text',
          tenant_id: tenantId,
        });

        showSuccess('Сообщение отправлено');
        if (onOpenConversation) {
          onOpenConversation(maxIdentifier);
        }
        handleClose();
      } catch (error: any) {
        console.error('Ошибка при отправке сообщения:', error);
        const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при отправке сообщения';
        showError(errorMessage);
        setBindingError(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Обычная логика для телефона
    if (!phone.trim()) {
      setPhoneError('Введите номер телефона');
      return;
    }
    
    const validation = validatePhone(phone);
    if (!validation.isValid) {
      setPhoneError(validation.error || 'Неверный формат телефона');
      return;
    }

    if (checkState === 'exists') {
      handleOpenExisting();
      return;
    }

    if (checkState === 'no_binding') {
      handleGoToBinding();
      return;
    }

    if (checkState !== 'ready' && checkState !== 'tg_search_found' && checkState !== 'max_search_found') {
      return;
    }
    
    setIsSubmitting(true);
    setBindingError('');

    try {
      const recipient =
        checkState === 'max_search_found' && foundMaxUser
          ? `max:${foundMaxUser.chat_id ?? foundMaxUser.user_id}`
          : validation.normalized!;

      await messagesApi.sendMessage({
        phone: recipient,
        channel: selectedChannel,
        content: messageText.trim(),
        content_type: 'text',
        tenant_id: tenantId,
      });

      showSuccess('Сообщение отправлено');
      
      if (onOpenConversation) {
        onOpenConversation(recipient);
      }
      
      handleClose();
    } catch (error: any) {
      console.error('Ошибка при отправке сообщения:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Ошибка при отправке сообщения';
      showError(errorMessage);
      setBindingError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Опции для выбора канала — показываем только подключенные (05.02.2026)
  const channelOptions = useMemo(() => {
    return INITIATABLE_CHANNELS
      .filter((channelId) => messengerStatus[channelId as keyof typeof messengerStatus])
      .map((channelId) => {
    const channel = CHANNELS[channelId];
    return {
      value: channelId,
      label: `${channel.icon} ${channel.name}`,
    };
  });
  }, [messengerStatus]);
  
  // Количество неподключенных каналов (для информации)
  const disconnectedCount = INITIATABLE_CHANNELS.length - channelOptions.length;

  const channelConfig = selectedChannel ? CHANNELS[selectedChannel as ChannelId] : null;
  const requiresBinding = channelConfig?.requiresBinding;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Начать диалог"
      size="md"
      testId="modal-initiate-conversation"
      footer={
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
            testId="btn-cancel-initiate"
          >
            Отмена
          </Button>
          {checkState === 'exists' ? (
            <Button
              variant="primary"
              onClick={handleOpenExisting}
              testId="btn-open-existing"
            >
              Открыть переписку
            </Button>
          ) : checkState === 'no_binding' ? (
            <Button
              variant="primary"
              onClick={handleGoToBinding}
              testId="btn-go-to-binding"
            >
              Перейти к привязке
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                !selectedChannel ||
                !messageText.trim() ||
                isSubmitting ||
                (selectedChannel === 'telegram_client' && inputMode === 'messenger_id'
                  ? checkState !== 'tg_found' || !foundTgUser
                  : selectedChannel === 'max_client' && inputMode === 'messenger_id'
                    ? checkState !== 'max_found' || !foundMaxUser
                    : !phone.trim() ||
                      !!phoneError ||
                      (checkState !== 'ready' &&
                        checkState !== 'tg_search_found' &&
                        checkState !== 'max_search_found'))
              }
              isLoading={isSubmitting}
              testId="btn-submit-initiate"
            >
              Отправить сообщение
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Иконка и описание */}
        <div className="flex items-center gap-3 text-slate-600">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700">
              Создать новый диалог с пациентом
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Введите номер телефона и выберите канал для отправки сообщения
            </p>
          </div>
        </div>

        {/* Переключатель режима ввода для TG / MAX Client */}
        {(selectedChannel === 'telegram_client' || selectedChannel === 'max_client') && (
          <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
            <button
              type="button"
              onClick={() => {
                setInputMode('phone');
                setTelegramError('');
                setMaxError('');
                setCheckState('idle');
                setFoundTgUser(null);
                setFoundMaxUser(null);
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                inputMode === 'phone'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Phone className="w-4 h-4" />
              Телефон
            </button>
            <button
              type="button"
              onClick={() => {
                setInputMode('messenger_id');
                setPhoneError('');
                setCheckState('idle');
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                inputMode === 'messenger_id'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <AtSign className="w-4 h-4" />
              {selectedChannel === 'max_client' ? 'MAX chat ID' : 'Telegram ID'}
            </button>
          </div>
        )}

        {/* Поле ввода телефона */}
        {!(
          (selectedChannel === 'telegram_client' || selectedChannel === 'max_client') &&
          inputMode === 'messenger_id'
        ) && (
          <Input
            label="Номер телефона"
            type="tel"
            placeholder="+7 (999) 123-45-67"
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            error={phoneError}
            testId="input-phone"
            autoFocus
          />
        )}

        {selectedChannel === 'telegram_client' && inputMode === 'messenger_id' && (
          <Input
            label="Telegram ID или username"
            type="text"
            placeholder="7104785699 или @SmileProSkills"
            value={telegramInput}
            onChange={(e) => {
              setTelegramInput(e.target.value);
              setTelegramError('');
              setCheckState('idle');
              setFoundTgUser(null);
            }}
            error={telegramError}
            testId="input-telegram-id"
            autoFocus
          />
        )}

        {selectedChannel === 'max_client' && inputMode === 'messenger_id' && (
          <Input
            label="MAX chat ID"
            type="text"
            placeholder="123456789 или max:123456789"
            value={maxInput}
            onChange={(e) => {
              setMaxInput(e.target.value);
              setMaxError('');
              setCheckState('idle');
              setFoundMaxUser(null);
            }}
            error={maxError}
            testId="input-max-chat-id"
            autoFocus
          />
        )}

        {/* Выбор канала */}
        <Select
          label="Канал отправки"
          value={selectedChannel}
          onChange={(e) => {
            setSelectedChannel(e.target.value);
            setPhoneError('');
            setTelegramError('');
            setMaxError('');
            setInputMode('phone');
            setCheckState('idle');
            setBindingError('');
            setMessageText('');
            setFoundTgUser(null);
            setFoundMaxUser(null);
          }}
          options={[
            { value: '', label: 'Выберите канал' },
            ...channelOptions,
          ]}
          testId="select-channel"
        />

        {/* Состояние проверки */}
        {checkState === 'checking' && (
          <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Проверка переписки и привязки...</span>
          </div>
        )}

        {/* Переписка существует */}
        {checkState === 'exists' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800 mb-1">
                Переписка уже существует
              </p>
              <p className="text-xs text-amber-700">
                С этим пациентом уже есть переписка по выбранному каналу. Вы можете открыть её или выбрать другой канал.
              </p>
            </div>
          </div>
        )}

        {/* Нужна привязка — для TG Client показываем кнопку поиска inline */}
        {checkState === 'no_binding' && selectedChannel === 'telegram_client' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50">
            <Search className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800 mb-1">
                Привязка к Telegram не найдена
              </p>
              <p className="text-xs text-blue-700 mb-3">
                Проверим, зарегистрирован ли пациент в Telegram по номеру телефона. Номер пациента должен быть открыт для поиска (настройки приватности).
              </p>
              <Button
                onClick={handleSearchInTg}
                variant="secondary"
                size="sm"
                disabled={!messengerStatus.telegram_client || isTgSearching}
                testId="btn-search-tg-initiate"
              >
                {isTgSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Поиск в Telegram…
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-1" />
                    Проверить в Telegram
                  </>
                )}
              </Button>
              {!messengerStatus.telegram_client && (
                <p className="text-xs text-amber-600 mt-2">
                  Telegram Client не подключён в настройках
                </p>
              )}
            </div>
          </div>
        )}

        {checkState === 'no_binding' && selectedChannel === 'max_client' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-fuchsia-200 bg-fuchsia-50">
            <Search className="w-5 h-5 text-fuchsia-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-fuchsia-800 mb-1">
                Привязка MAX Client не найдена
              </p>
              <p className="text-xs text-fuchsia-700 mb-3">
                Проверим, зарегистрирован ли пациент в MAX по номеру телефона (phone-search через go-max-client).
              </p>
              <Button
                onClick={() => void handleSearchInMax()}
                variant="secondary"
                size="sm"
                disabled={!messengerStatus.max_client || isMaxSearching}
                testId="btn-search-max-initiate"
              >
                {isMaxSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Поиск в MAX…
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-1" />
                    Проверить в MAX
                  </>
                )}
              </Button>
              {!messengerStatus.max_client && (
                <p className="text-xs text-amber-600 mt-2">
                  MAX Client не подключён в настройках
                </p>
              )}
            </div>
          </div>
        )}

        {/* Нужна привязка — для остальных каналов (бот, MAX Bot) */}
        {checkState === 'no_binding' &&
          selectedChannel !== 'telegram_client' &&
          selectedChannel !== 'max_client' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-orange-200 bg-orange-50">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 mb-1">
                Требуется привязка пациента
              </p>
              <p className="text-xs text-orange-700 mb-3">
                {selectedChannel === 'telegram_bot'
                  ? 'Пациент должен написать в бота и быть привязан. Отправьте пациенту ссылку на бота.'
                  : `Для отправки сообщений через ${channelConfig?.name} необходимо привязать пациента к мессенджеру.`}
              </p>
              <button
                onClick={handleGoToBinding}
                className="text-xs text-orange-700 hover:text-orange-800 font-medium flex items-center gap-1 underline"
              >
                Перейти к привязке
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* TG поиск по телефону — найден */}
        {checkState === 'tg_search_found' && tgPhoneSearchResult && (
          <>
            <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-200 bg-emerald-50">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800 mb-1">
                  Пользователь найден и привязан
                </p>
                <div className="text-xs text-emerald-700 space-y-0.5">
                  {tgPhoneSearchResult.first_name && (
                    <p><strong>Имя:</strong> {tgPhoneSearchResult.first_name} {tgPhoneSearchResult.last_name || ''}</p>
                  )}
                  {tgPhoneSearchResult.telegram_username && (
                    <p><strong>Username:</strong> @{tgPhoneSearchResult.telegram_username}</p>
                  )}
                  {tgPhoneSearchResult.telegram_user_id && (
                    <p><strong>ID:</strong> {tgPhoneSearchResult.telegram_user_id}</p>
                  )}
                </div>
              </div>
            </div>

            <Textarea
              label="Текст сообщения"
              placeholder="Введите текст сообщения..."
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                setBindingError('');
              }}
              error={bindingError}
              rows={4}
              testId="textarea-message-tg-search"
            />
          </>
        )}

        {/* TG поиск по телефону — не найден */}
        {checkState === 'tg_search_not_found' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 mb-1">
                Не удалось найти в Telegram
              </p>
              <p className="text-xs text-red-700 mb-3">
                Пациент не зарегистрирован в Telegram или настройки приватности запрещают поиск по номеру.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handleSearchInTg}
                  variant="secondary"
                  size="sm"
                  disabled={isTgSearching}
                  testId="btn-retry-search-tg"
                >
                  {isTgSearching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Повтор…
                    </>
                  ) : (
                    'Повторить поиск'
                  )}
                </Button>
                <button
                  onClick={handleGoToBinding}
                  className="text-xs text-slate-600 hover:text-slate-800 font-medium flex items-center gap-1 underline"
                >
                  Привязка другим способом
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TG пользователь найден — показываем карточку и поле для сообщения */}
        {checkState === 'tg_found' && foundTgUser && (
          <>
            <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-200 bg-emerald-50">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800 mb-1">
                  Пользователь найден в Telegram
                </p>
                <div className="text-xs text-emerald-700 space-y-0.5">
                  <p><strong>Имя:</strong> {foundTgUser.first_name} {foundTgUser.last_name || ''}</p>
                  {foundTgUser.username && <p><strong>Username:</strong> @{foundTgUser.username}</p>}
                  <p><strong>ID:</strong> {foundTgUser.user_id}</p>
                  {foundTgUser.phone && <p><strong>Телефон:</strong> {foundTgUser.phone}</p>}
                </div>
              </div>
            </div>

            {/* Текст сообщения для TG */}
            <Textarea
              label="Текст сообщения"
              placeholder="Введите текст сообщения..."
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                setBindingError('');
              }}
              error={bindingError}
              rows={4}
              testId="textarea-message-tg"
            />
          </>
        )}

        {checkState === 'max_search_found' && foundMaxUser && (
          <>
            <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-200 bg-emerald-50">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800 mb-1">
                  Пользователь найден в MAX
                </p>
                <div className="text-xs text-emerald-700 space-y-0.5">
                  {(foundMaxUser.first_name || foundMaxUser.last_name) && (
                    <p>
                      <strong>Имя:</strong> {foundMaxUser.first_name || ''} {foundMaxUser.last_name || ''}
                    </p>
                  )}
                  <p><strong>Chat ID:</strong> {foundMaxUser.chat_id ?? foundMaxUser.user_id}</p>
                </div>
              </div>
            </div>

            <Textarea
              label="Текст сообщения"
              placeholder="Введите текст сообщения..."
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                setBindingError('');
              }}
              error={bindingError}
              rows={4}
              testId="textarea-message-max-search"
            />
          </>
        )}

        {checkState === 'max_search_not_found' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 mb-1">
                Не удалось найти в MAX
              </p>
              <p className="text-xs text-red-700 mb-3">
                Пользователь не найден по номеру телефона. Попробуйте режим «MAX chat ID», если известен числовой ID.
              </p>
              <Button
                onClick={() => void handleSearchInMax()}
                variant="secondary"
                size="sm"
                disabled={isMaxSearching}
                testId="btn-retry-search-max"
              >
                {isMaxSearching ? 'Повтор…' : 'Повторить поиск'}
              </Button>
            </div>
          </div>
        )}

        {checkState === 'max_found' && foundMaxUser && (
          <>
            <div className="flex items-start gap-3 p-4 rounded-lg border border-emerald-200 bg-emerald-50">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800 mb-1">
                  MAX chat ID принят
                </p>
                <div className="text-xs text-emerald-700 space-y-0.5">
                  <p><strong>Chat ID:</strong> {foundMaxUser.chat_id ?? foundMaxUser.user_id}</p>
                  {(foundMaxUser.first_name || foundMaxUser.last_name) && (
                    <p>
                      <strong>Имя:</strong> {foundMaxUser.first_name || ''} {foundMaxUser.last_name || ''}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Textarea
              label="Текст сообщения"
              placeholder="Введите текст сообщения..."
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                setBindingError('');
              }}
              error={bindingError}
              rows={4}
              testId="textarea-message-max"
            />
          </>
        )}

        {checkState === 'max_not_found' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 mb-1">
                Некорректный MAX chat ID
              </p>
              <p className="text-xs text-red-700">
                {maxError || 'Введите числовой chat ID. @username в MAX не поддерживается.'}
              </p>
            </div>
          </div>
        )}

        {/* TG пользователь не найден */}
        {checkState === 'tg_not_found' && (
          <div className="flex items-start gap-3 p-4 rounded-lg border border-red-200 bg-red-50">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 mb-1">
                Пользователь не найден
              </p>
              <p className="text-xs text-red-700">
                {telegramError || 'Проверьте правильность Telegram ID или username. Для поиска по телефону переключитесь в режим "Телефон".'}
              </p>
            </div>
          </div>
        )}

        {/* Готово к отправке (режим телефона) */}
        {checkState === 'ready' && (
          <>
            {requiresBinding && (
              <div className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-emerald-700">
                    <strong>Привязка подтверждена.</strong> Можно отправлять сообщение.
                  </p>
                </div>
              </div>
            )}

            {/* Текст сообщения */}
            <Textarea
              label="Текст сообщения"
              placeholder="Введите текст сообщения..."
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                setBindingError('');
              }}
              error={bindingError}
              rows={4}
              testId="textarea-message"
            />

            {/* Подсказка для WABA */}
            {selectedChannel === 'whatsapp_waba' && (
              <div className="text-xs text-slate-500 bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="font-medium mb-1 text-blue-800">Важно для WhatsApp (WABA):</p>
                <p className="text-blue-700">
                  Если прошло более 24 часов с последнего сообщения от пациента, сообщение будет отправлено через шаблон.
                  Убедитесь, что используете одобренный шаблон.
                </p>
              </div>
            )}

            {/* Подсказка для WhatsApp Regular */}
            {selectedChannel === 'whatsapp_regular' && (
              <div className="text-xs text-slate-500 bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="font-medium mb-1 text-green-800">WhatsApp (Regular):</p>
                <p className="text-green-700">
                  Сообщение будет отправлено напрямую через Wazzup API. Можно писать первым без ожидания ответа от пациента.
                </p>
              </div>
            )}
          </>
        )}

        {/* Общая подсказка */}
        {checkState === 'idle' && channelOptions.length > 0 && (
          <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-200">
            <p className="font-medium mb-1">Подключенные каналы ({channelOptions.length}):</p>
            <ul className="list-disc list-inside space-y-0.5">
              {messengerStatus.whatsapp_waba && (
              <li><strong>WhatsApp (WABA)</strong> — отправка через шаблон (вне 24h окна)</li>
              )}
              {messengerStatus.whatsapp_regular && (
              <li><strong>WhatsApp (Regular)</strong> — можно писать первым через Wazzup</li>
              )}
              {messengerStatus.telegram_bot && (
              <li><strong>Telegram Bot</strong> — пациент должен написать в бота и быть привязан</li>
              )}
              {messengerStatus.telegram_client && (
              <li><strong>Telegram Client</strong> — номер пациента должен быть открыт для поиска в TG</li>
              )}
              {messengerStatus.max_client && (
              <li><strong>MAX Client</strong> — phone-search или числовой chat ID (без @username)</li>
              )}
              {messengerStatus.max && (
              <li><strong>MAX (VK)</strong> — требуется привязка пациента к VK</li>
              )}
              {messengerStatus.sms && (
              <li><strong>SMS</strong> — отправка напрямую по номеру</li>
              )}
            </ul>
          </div>
        )}
        
        {/* Предупреждение о неподключенных каналах (05.02.2026) */}
        {disconnectedCount > 0 && checkState === 'idle' && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">
                {disconnectedCount} {disconnectedCount === 1 ? 'канал не подключен' : 'каналов не подключено'}
              </p>
              <p className="mt-0.5 text-amber-600">
                Для подключения дополнительных каналов перейдите в{' '}
                <button
                  onClick={() => {
                    handleClose();
                    navigate('/settings');
                  }}
                  className="font-medium underline hover:text-amber-800"
                >
                  Настройки
                </button>
              </p>
            </div>
          </div>
        )}
        
        {/* Нет подключенных каналов */}
        {channelOptions.length === 0 && !isConfigLoading && (
          <div className="flex flex-col items-center justify-center p-6 text-center rounded-xl bg-red-50 border border-red-200">
            <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
            <h3 className="text-lg font-semibold text-red-700 mb-2">
              Нет подключенных каналов
            </h3>
            <p className="text-sm text-red-600 mb-4 max-w-sm">
              Для отправки сообщений необходимо подключить хотя бы один мессенджер в настройках клиники.
            </p>
            <button
              onClick={() => {
                handleClose();
                navigate('/settings');
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-red-700 font-medium shadow-sm hover:bg-red-50 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Перейти в настройки
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
